import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";

/** Extract obligee id from bond retrieve API response. */
export function getObligeeIdFromResponse(data, nestedKey) {
  if (data == null) return undefined;
  const raw = data.obligee ?? data.obligee_id ?? data[nestedKey]?.obligee;
  if (raw == null) return undefined;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && /^\d+$/.test(String(raw).trim())) {
    return Number(raw);
  }
  if (typeof raw === "object" && raw !== null) {
    return raw.obligee_id ?? raw.id ?? undefined;
  }
  return undefined;
}

/** Shared chat drawer state for bond retrieve pages. */
export function useBondRetrievalChat() {
  const [openChat, setOpenChat] = useState(false);
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
  return { openChat, setOpenChat, chatUnreadTotal, setChatUnreadTotal };
}

/** Poll chat unread totals for Navbar badge on retrieve pages. */
export function useBondRetrievalChatUnread(bondId, role, setChatUnreadTotal) {
  useEffect(() => {
    if (!bondId) return;
    let isMounted = true;
    const fetchCounts = async () => {
      try {
        const counts = await apiClient(`/messages-recent-by-sender/${bondId}`, "GET");
        if (!isMounted) return;
        const total = Object.entries(counts || {}).reduce((sum, [senderType, count]) => {
          return senderType === role ? sum : sum + (Number(count) || 0);
        }, 0);
        setChatUnreadTotal(total);
      } catch {
        /* ignore transient poll errors */
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [bondId, role, setChatUnreadTotal]);
}

/** Shared endorsement history drawer for bond retrieve pages. */
export function useBondRetrievalEndorsementHistory(bondId) {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [endorsementLogs, setEndorsementLogs] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState(0);

  const fetchEndorsementLogs = useCallback(async () => {
    if (!bondId) return;
    try {
      setIsHistoryLoading(true);
      const response = await apiClient(`/endorsement-logs/${bondId}/`);
      setEndorsementLogs(response);
    } catch (error) {
      toast.error("Failed to load endorsement history");
      console.error(error);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [bondId]);

  const toggleHistoryDrawer = useCallback(async () => {
    if (!isHistoryOpen) {
      await fetchEndorsementLogs();
    }
    setIsHistoryOpen((prev) => !prev);
  }, [isHistoryOpen, fetchEndorsementLogs]);

  return {
    isHistoryOpen,
    setIsHistoryOpen,
    endorsementLogs,
    setEndorsementLogs,
    isHistoryLoading,
    setIsHistoryLoading,
    historyTab,
    setHistoryTab,
    fetchEndorsementLogs,
    toggleHistoryDrawer,
  };
}

/**
 * VA obligee: preserve user edits when re-applying API data for the same bond.
 */
export function useVaObligeeResolver(bondId) {
  const obligeeAppliedForBondIdRef = useRef(null);

  const resolveVaObligeeInformation = useCallback((data, prevState) => {
    const alreadyAppliedForThisBond = obligeeAppliedForBondIdRef.current === bondId;
    const hasExistingObligee =
      prevState.obligeeInformation?.obligeeId != null &&
      String(prevState.obligeeInformation.obligeeId).trim() !== "";
    if (!alreadyAppliedForThisBond) obligeeAppliedForBondIdRef.current = bondId;
    const apiObligeeId = getObligeeIdFromResponse(data, "vabond");
    const obligeeInformation = hasExistingObligee && alreadyAppliedForThisBond
      ? prevState.obligeeInformation
      : { obligeeId: apiObligeeId };
    return obligeeInformation;
  }, [bondId]);

  return { obligeeAppliedForBondIdRef, resolveVaObligeeInformation, getObligeeIdFromResponse };
}

export function resolveObligeeInformation(data, nestedKey, _logLabel) {
  const apiObligeeId = getObligeeIdFromResponse(data, nestedKey);
  return { obligeeId: apiObligeeId };
}
